import { useState, useRef, useEffect, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { ChevronRight, Check, Settings, Copy, Trash2 } from 'lucide-react';
import { useProject, getProjectShortId } from '../../contexts/ProjectContext';
import { useProjectNavigation } from '../../hooks/useProjectNavigation';
import { projectsApi } from '../../api/projects';

/**
 * Minimal Project Selector
 * Subtle inline project switcher - users don't switch projects often
 * so this stays out of the way until needed.
 * Hovering the chevron on each project row opens a Braintrust-style submenu.
 */
export const ProjectSelector = () => {
  const { project, projects, setProjectId, isLoading, allProjects, refreshProjects, buildProjectUrl } = useProject();
  const { getCurrentPathSegment, isProjectScoped, navigateToOtherProject } = useProjectNavigation();
  const [isOpen, setIsOpen] = useState(false);
  const [submenuProjectId, setSubmenuProjectId] = useState<string | null>(null);
  const [copiedProjectId, setCopiedProjectId] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const submenuTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const rowRefs = useRef<Map<string, HTMLDivElement | null>>(new Map());
  const navigate = useNavigate();
  const location = useLocation();

  // Handle project selection with navigation
  const handleProjectSelect = (projectId: string) => {
    const selectedProject = allProjects.find(p => p.id === projectId);
    setProjectId(projectId);
    closeAll();

    if (!selectedProject) return;

    const newShortId = getProjectShortId(selectedProject.id);

    if (isProjectScoped) {
      // Only carry the top-level route segment (e.g. "do", "bo", "activity"),
      // NOT sub-paths like "do/eknmaxu" — document/item IDs are project-specific
      const currentPath = getCurrentPathSegment().split('/')[0] || '';
      navigateToOtherProject(newShortId, currentPath);
    } else if (location.pathname.startsWith('/tracker')) {
      navigateToOtherProject(newShortId, 'bo');
    } else {
      navigate(buildProjectUrl('', 'pr').replace(/\/$/, ''));
    }
  };

  const closeAll = useCallback(() => {
    setIsOpen(false);
    setSubmenuProjectId(null);
    setCopiedProjectId(null);
    if (submenuTimeoutRef.current) {
      clearTimeout(submenuTimeoutRef.current);
      submenuTimeoutRef.current = null;
    }
  }, []);

  // Submenu hover handlers with small delay to prevent flicker
  const handleChevronEnter = useCallback((projectId: string) => {
    if (submenuTimeoutRef.current) {
      clearTimeout(submenuTimeoutRef.current);
      submenuTimeoutRef.current = null;
    }
    setSubmenuProjectId(projectId);
  }, []);

  const handleChevronLeave = useCallback(() => {
    submenuTimeoutRef.current = setTimeout(() => {
      setSubmenuProjectId(null);
      setCopiedProjectId(null);
    }, 150);
  }, []);

  const handleSubmenuEnter = useCallback(() => {
    if (submenuTimeoutRef.current) {
      clearTimeout(submenuTimeoutRef.current);
      submenuTimeoutRef.current = null;
    }
  }, []);

  const handleSubmenuLeave = useCallback(() => {
    submenuTimeoutRef.current = setTimeout(() => {
      setSubmenuProjectId(null);
      setCopiedProjectId(null);
    }, 150);
  }, []);

  // Submenu actions
  const handleProjectSettings = useCallback((projectId: string) => {
    const shortId = getProjectShortId(projectId);
    closeAll();
    navigate(buildProjectUrl('settings'));
  }, [navigate, closeAll]);

  const handleCopyProjectId = useCallback(async (projectId: string) => {
    try {
      await navigator.clipboard.writeText(projectId);
      setCopiedProjectId(projectId);
      setTimeout(() => {
        setCopiedProjectId(null);
      }, 1500);
    } catch (err) {
      console.error('Failed to copy project ID:', err);
    }
  }, []);

  const handleDeleteProject = useCallback(async (proj: { id: string; name: string }) => {
    const confirmed = window.confirm(`Delete project "${proj.name}"? This cannot be undone.`);
    if (!confirmed) return;

    try {
      await projectsApi.delete(proj.id);
      closeAll();
      await refreshProjects();
    } catch (err) {
      console.error('Failed to delete project:', err);
    }
  }, [closeAll, refreshProjects]);

  // Compute submenu position based on the hovered row
  const getSubmenuTop = useCallback((): number => {
    if (!submenuProjectId) return 0;
    const rowEl = rowRefs.current.get(submenuProjectId);
    const containerEl = containerRef.current;
    if (!rowEl || !containerEl) return 0;
    const dropdownEl = containerEl.querySelector('[data-dropdown]') as HTMLElement | null;
    if (!dropdownEl) return 0;
    const rowRect = rowEl.getBoundingClientRect();
    const dropdownRect = dropdownEl.getBoundingClientRect();
    return rowRect.top - dropdownRect.top;
  }, [submenuProjectId]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        closeAll();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [closeAll]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (submenuTimeoutRef.current) {
        clearTimeout(submenuTimeoutRef.current);
      }
    };
  }, []);

  // Loading state - minimal
  if (isLoading) {
    return (
      <div className="px-5 py-1.5">
        <div className="h-4 w-24 bg-surface-subtle rounded animate-pulse" />
      </div>
    );
  }

  // No projects state - link to create
  if (allProjects.length === 0) {
    return (
      <div className="px-5 py-1.5">
        <Link
          to="/projects"
          className="text-[11px] text-subtle hover:text-coral-500 transition-colors"
        >
          + Create first project
        </Link>
      </div>
    );
  }

  // No projects in current pane
  if (projects.length === 0) {
    return (
      <div className="px-5 py-1.5">
        <Link
          to="/projects"
          className="text-[11px] text-subtle hover:text-coral-500 transition-colors"
        >
          No projects assigned — manage projects
        </Link>
      </div>
    );
  }

  return (
    <div ref={containerRef} className="relative px-5 py-1.5">
      {/* Project name + settings gear */}
      <div className="flex items-center gap-1.5">
        <button
          data-testid="project-selector"
          onClick={() => setIsOpen(!isOpen)}
          className="group flex items-center gap-1 text-[11px] text-muted
                     hover:text-text transition-colors min-w-0"
        >
          <span className="font-medium truncate max-w-[140px]">
            {project?.name || 'Select project'}
          </span>
          <ChevronRight
            size={12}
            className={`flex-shrink-0 text-subtle group-hover:text-subtle transition-all duration-150
                       ${isOpen ? 'rotate-90' : ''}`}
          />
        </button>
        {project && (
          <Link
            to={buildProjectUrl('settings')}
            className="flex-shrink-0 p-0.5 rounded text-subtle hover:text-muted
                       hover:bg-surface-subtle transition-colors"
            title="Project settings"
            onClick={(e) => e.stopPropagation()}
          >
            <Settings size={12} />
          </Link>
        )}
      </div>

      {/* Dropdown - compact and clean */}
      {isOpen && (
        <>
          {/* Backdrop */}
          <div className="fixed inset-0 z-40" onClick={closeAll} />

          {/* Menu */}
          <div
            data-dropdown
            className="absolute left-0 top-full mt-1 z-50 min-w-[200px] max-w-[260px]
                        bg-white rounded-lg shadow-lg border border-line/80
                        py-1 animate-fade-in"
            style={{ animationDuration: '100ms' }}
          >
            {/* Quick list of projects */}
            <div className="max-h-[200px] overflow-y-auto">
              {projects.map((p) => {
                const isSelected = p.id === project?.id;
                return (
                  <div
                    key={p.id}
                    ref={(el) => { rowRefs.current.set(p.id, el); }}
                    className={`relative flex items-center text-[12px] transition-colors
                               ${isSelected
                                 ? 'bg-coral-50 text-coral-700'
                                 : 'text-muted hover:bg-surface-muted'}`}
                  >
                    {/* Left part: check + name — selects the project */}
                    <button
                      data-testid={`project-option-${p.slug}`}
                      onClick={() => handleProjectSelect(p.id)}
                      className="flex-1 flex items-center gap-2 px-3 py-1.5 text-left min-w-0"
                    >
                      {isSelected ? (
                        <Check size={12} className="text-coral-500 flex-shrink-0" />
                      ) : (
                        <span className="w-3 flex-shrink-0" />
                      )}
                      <span className="truncate">{p.name}</span>
                    </button>

                    {/* Right part: chevron — opens submenu */}
                    <div
                      className="flex items-center justify-center w-7 h-full flex-shrink-0
                                 cursor-pointer hover:bg-surface-subtle rounded-r transition-colors"
                      onMouseEnter={() => handleChevronEnter(p.id)}
                      onMouseLeave={handleChevronLeave}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSubmenuProjectId(submenuProjectId === p.id ? null : p.id);
                      }}
                    >
                      <ChevronRight
                        size={12}
                        className={`transition-colors ${
                          submenuProjectId === p.id
                            ? 'text-muted'
                            : 'text-subtle'
                        }`}
                      />
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Divider + manage link */}
            <div className="border-t border-line mt-1 pt-1">
              <Link
                to="/projects"
                onClick={closeAll}
                className="block px-3 py-1.5 text-[11px] text-subtle
                          hover:text-coral-500 hover:bg-surface-muted transition-colors"
              >
                Manage projects...
              </Link>
            </div>

            {/* Submenu — positioned to the right of the dropdown */}
            {submenuProjectId && (() => {
              const submenuProject = allProjects.find(p => p.id === submenuProjectId);
              if (!submenuProject) return null;
              const topOffset = getSubmenuTop();
              return (
                <div
                  className="absolute min-w-[160px] bg-white rounded-lg shadow-lg
                             border border-line/80 py-1 animate-fade-in"
                  style={{
                    left: '100%',
                    top: topOffset,
                    marginLeft: 4,
                    animationDuration: '80ms',
                  }}
                  onMouseEnter={handleSubmenuEnter}
                  onMouseLeave={handleSubmenuLeave}
                >
                  {/* Project settings */}
                  <button
                    onClick={() => handleProjectSettings(submenuProject.id)}
                    className="w-full flex items-center gap-2 px-3 py-1.5 text-left text-[12px]
                               text-muted hover:bg-surface-muted transition-colors"
                  >
                    <Settings size={12} className="text-subtle flex-shrink-0" />
                    <span>Project settings</span>
                  </button>

                  {/* Copy project ID */}
                  <button
                    onClick={() => handleCopyProjectId(submenuProject.id)}
                    className="w-full flex items-center gap-2 px-3 py-1.5 text-left text-[12px]
                               text-muted hover:bg-surface-muted transition-colors"
                  >
                    <Copy size={12} className="text-subtle flex-shrink-0" />
                    <span>{copiedProjectId === submenuProject.id ? 'Copied!' : 'Copy project ID'}</span>
                  </button>

                  {/* Delete project */}
                  <div className="border-t border-line mt-1 pt-1">
                    <button
                      onClick={() => handleDeleteProject(submenuProject)}
                      className="w-full flex items-center gap-2 px-3 py-1.5 text-left text-[12px]
                                 text-red-500 hover:bg-red-50 transition-colors"
                    >
                      <Trash2 size={12} className="flex-shrink-0" />
                      <span>Delete project</span>
                    </button>
                  </div>
                </div>
              );
            })()}
          </div>
        </>
      )}
    </div>
  );
};
